import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
} from "recharts";
import { Grid, Typography, Box, IconButton, Divider } from "@mui/material";
import { format, parseISO } from "date-fns";
import { WidgetContainer } from "../widgetContainer/widgetContainer";
import { DownloadExcel } from "../../../functions/downloadExcel";
import { useTheme } from "@mui/material/styles";
import {
  adjustColorBrightness,
  CustomMonthYearTick,
  CustomTooltip,
  CustomXTick,
  CustomYTick,
  getDistinctColor,
  hashKeyToColor,
  renderCustomLegend,
  getVariantColor,
} from "./lineGraph.logic";

const LineGraph = ({
  data,
  title,
  currency = false,
  percentage = false,
  xInterval = 0,
  interval = 20000,
  splitX = false,
  discription = "",
  onDataPointClick = null,
  insights = false,
  isPercentage = false,
  draggable = false,
  editMode = false,
  defaultProvider = "ALL",
  showAll = false,
  loadingMessage = "Loading...",
  onInsights = () => {},
  onDownload = () => {},
  downloadTooltip = "Download",
  downloadDropdown = false,
  showProjection = false,
  projectedValue = null,
  projectionLineKey = null, // Which line should show the projection (e.g., "TOTAL", "ALL - TOTAL")
  showMeanLine = false, // Toggle to show mean/run rate line
}) => {
  const theme = useTheme();

  const isValidDataObject = (d) => d && typeof d === "object" && !Array.isArray(d);
  const [selectedProvider, setSelectedProvider] = useState(defaultProvider);
  const [lastUpdatedDate, setLastUpdatedDate] = useState("");
  const [isMeanLineEnabled, setIsMeanLineEnabled] = useState(false);
  const providers = isValidDataObject(data) ? [...Object.keys(data)] : [];

  useEffect(() => {
    if (!isValidDataObject(data) || lastUpdatedDate) return;
    const firstKey = Object.keys(data)[0];
    const timestamp = data[firstKey]?.[0]?.timestamp;
    if (timestamp) {
      const date = parseISO(timestamp);
      const day = parseInt(format(date, "d"), 10);
      const ordinal = (day) => {
        if (day >= 11 && day <= 13) return `${day}th`;
        switch (day % 10) {
          case 1:
            return `${day}st`;
          case 2:
            return `${day}nd`;
          case 3:
            return `${day}rd`;
          default:
            return `${day}th`;
        }
      };
      const formatted = `${format(date, "h:mm a EEE")} ${ordinal(day)} of ${format(date, "MMMM")}`;
      setLastUpdatedDate(formatted);
    }
  }, [data, lastUpdatedDate]);

  useEffect(() => {
    if (providers.length < 2) return;
    const cycleProviders = () => {
      setSelectedProvider((prev) => {
        const currentIndex = providers.indexOf(prev);
        const nextIndex = (currentIndex + 1) % providers.length;
        return providers[nextIndex];
      });
    };
    const intervalId = setInterval(cycleProviders, interval);
    return () => clearInterval(intervalId);
  }, [providers, interval]);

  if (!isValidDataObject(data) || providers.length === 0) {
    return (
      <WidgetContainer
        loadingMessage={loadingMessage}
        editMode={editMode}
        title={title}
        description={discription}
        loaded={false}
        draggable={draggable}
        onInsights={onInsights}
        onDownload={onDownload}
      >
        <Typography sx={{ color: "#fff", p: 2 }}>No data available to display.</Typography>
      </WidgetContainer>
    );
  }

  const allKeys = new Set();

  const finalColors = {};
  Object.values(data).forEach((value) => {
    if (Array.isArray(value)) {
      allKeys.add(value.label || value.name || "default");
    } else if (typeof value === "object") {
      Object.entries(value).forEach(([subKey]) => {
        allKeys.add(`${subKey}`);
      });
    }
  });

  const isMultiLevel =
    data[selectedProvider] &&
    typeof data[selectedProvider] === "object" &&
    !Array.isArray(data[selectedProvider]) &&
    Object.values(data[selectedProvider]).every((v) => Array.isArray(v));

  const chartData = (() => {
    if (selectedProvider === "ALL" && showAll) {
      const map = {};

      Object.entries(data).forEach(([provider, series]) => {
        if (Array.isArray(series)) {
          series.forEach((entry) => {
            const key = entry.month || entry.dateKey || entry.timestamp;
            if (!key) return;
            if (!map[key]) map[key] = { month: key };
            map[key][provider] = entry.value ?? 0;
          });
        } else if (typeof series === "object") {
          Object.entries(series).forEach(([subKey, records]) => {
            if (!Array.isArray(records)) return;
            records.forEach((entry) => {
              const key = entry.month || entry.dateKey || entry.timestamp;
              if (!key) return;
              if (!map[key]) map[key] = { month: key };
              map[key][`${provider} - ${subKey}`] = entry.value ?? 0;
            });
          });
        }
      });

      return Object.values(map).sort(
        (a, b) => new Date(a.timestamp || a.month) - new Date(b.timestamp || b.month)
      );
    }

    if (isMultiLevel) {
      const map = {};
      Object.entries(data[selectedProvider] || {}).forEach(([subKey, records]) => {
        if (!Array.isArray(records)) return;
        records.forEach((entry) => {
          const key = entry.month || entry.dateKey || entry.timestamp;
          if (!key) return;
          if (!map[key]) map[key] = { month: key };
          map[key][`${selectedProvider} - ${subKey}`] = entry.value ?? 0;
        });
      });
      return Object.values(map).sort(
        (a, b) => new Date(a.timestamp || a.month) - new Date(b.timestamp || b.month)
      );
    }

    if (Array.isArray(data[selectedProvider])) {
      return data[selectedProvider].map((item) => ({
        [item.dateKey || "month"]: item[item.dateKey || "month"],
        value: typeof item.value === "number" ? item.value : 0,
        unit: item.unit,
      }));
    }

    return [];
  })();

  const dynamicDataKey = chartData[0]?.month ? "month" : Object.keys(chartData[0] || {})[0];
  const yAxisLabel = (chartData?.[0]?.unit || "").toUpperCase();

  // Add projection data to the current month if enabled
  let filteredData = chartData;
  if (showProjection && projectedValue && projectionLineKey) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthNum = now.getMonth();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    filteredData = chartData.map((dataPoint) => {
      const month = dataPoint.month || "";
      const isCurrentMonth =
        month.includes(`${currentYear}`) &&
        (month.includes(`-${String(currentMonthNum + 1).padStart(2, "0")}`) ||
          month.includes(monthNames[currentMonthNum]));

      if (isCurrentMonth) {
        // Find the matching line key (case-insensitive exact match only)
        // Also check for 'value' key when viewing single provider
        const matchingKey = Object.keys(dataPoint).find((key) => {
          if (key === "month") return false;
          return (
            key.toLowerCase() === projectionLineKey.toLowerCase() ||
            (key === "value" && selectedProvider.toLowerCase() === projectionLineKey.toLowerCase())
          );
        });

        if (matchingKey) {
          const newPoint = {
            ...dataPoint,
            [`${matchingKey}_projected`]: projectedValue,
          };
          return newPoint;
        }
      }
      return dataPoint;
    });
  }

  const allLineKeys = [
    ...new Set(filteredData.flatMap((d) => Object.keys(d))),
  ].filter((k) => k !== dynamicDataKey && !k.endsWith("_projected"));

  if (showProjection) {
    const novData = filteredData.find((d) => d.month && d.month.includes("Nov"));
  }

  allLineKeys.forEach((key, index) => {
    finalColors[key] = getDistinctColor(key, index);
  });

  const calculateDomain = () => {
    let min = Infinity;
    let max = -Infinity;

    chartData.forEach((entry) => {
      Object.entries(entry).forEach(([key, val]) => {
        if (key !== "month" && typeof val === "number" && !isNaN(val)) {
          min = Math.min(min, val);
          max = Math.max(max, val);
        }
      });
    });

    // Include projected value in domain calculation
    if (showProjection && projectedValue) {
      min = Math.min(min, projectedValue);
      max = Math.max(max, projectedValue);
    }

    if (!isFinite(min) || !isFinite(max)) return [0, 100]; // Fallback

    return percentage
      ? [Math.max(0, Math.floor(min) - 0.2), Math.min(100, Math.ceil(max) + 0.2)]
      : [Math.floor(min), Math.ceil(max)];
  };

  // Calculate mean value for Total line across ALL data (not just current view)
  const calculateMeanValue = () => {
    if (!showMeanLine || !isMeanLineEnabled) return null;

    // Always calculate mean from the original Total data, regardless of selected provider
    const totalData = data?.Total;
    if (!totalData || !Array.isArray(totalData)) return null;

    const values = totalData
      .map((entry) => entry.value)
      .filter((val) => typeof val === "number" && !isNaN(val));

    if (values.length === 0) return null;

    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  const meanValue = calculateMeanValue();

  const formatValue = (val) => {
    if (percentage) return `${val}%`;
    if (currency) {
      if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(0)}M`;
      if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
      return `$${val}`;
    }
    return val;
  };

  const handleDataPointClick = (event, payload) => {
    if (onDataPointClick) onDataPointClick(payload);
  };
  const providerLineCounts = {}; // add this before rendering lines

  Object.entries(data).forEach(([provider, series]) => {
    if (Array.isArray(series)) {
      providerLineCounts[provider] = 1;
    } else if (typeof series === "object") {
      providerLineCounts[provider] = Object.keys(series).length;
    }
  });

  return (
    <WidgetContainer
      title={title}
      description={discription}
      loaded={true}
      updatedAt={lastUpdatedDate}
      maximize={true}
      draggable={draggable}
      editMode={editMode}
      download={true}
      downloadTooltip={downloadTooltip}
      downloadDropdown={downloadDropdown}
      insights={insights}
      onInsights={onInsights}
      onDownload={
        onDownload && onDownload !== (() => {})
          ? onDownload
          : () => DownloadExcel(title, filteredData)
      }
    >
      <Box display="flex" flexDirection="row" height="100%" width="calc(100% - 70px)">
        <Grid container>
          <Box flex={1} mt={1}>
            <ResponsiveContainer height="98%" width="100%">
              <LineChart data={filteredData} margin={{ top: 5, right: 20, left: 20, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  interval={xInterval}
                  dataKey={dynamicDataKey}
                  tick={(props) => <CustomXTick {...props} theme={theme} />}
                />
                <YAxis
                  domain={calculateDomain()}
                  padding={{ top: 10, bottom: 10 }}
                  tick={{ fill: theme.palette.text.primary, fontWeight: "bold" }}
                  tickFormatter={formatValue}
                  label={{
                    value: yAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    offset: -10,
                    style: {
                      textAnchor: "middle",
                      fill: theme.palette.text.primary,
                      fontWeight: "bold",
                    },
                  }}
                />
                <Tooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      currency={currency}
                      isPercentage={isPercentage}
                      theme={theme}
                    />
                  )}
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                />

                {/* Highlight current month with blue column - only when viewing the specific projection line */}
                {showProjection &&
                  projectedValue &&
                  projectionLineKey &&
                  (() => {
                    // Only show the bar if we're viewing the specific projection line (not ALL)
                    const shouldShowBar =
                      selectedProvider.toLowerCase() === projectionLineKey.toLowerCase();

                    if (!shouldShowBar) return null;

                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const currentMonthNum = now.getMonth(); // 0-11
                    const monthNames = [
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ];

                    const currentMonthIndex = filteredData.findIndex((d) => {
                      const month = d.month || "";
                      return (
                        month.includes(`${currentYear}`) &&
                        (month.includes(`-${String(currentMonthNum + 1).padStart(2, "0")}`) ||
                          month.includes(monthNames[currentMonthNum]))
                      );
                    });

                    if (currentMonthIndex >= 0) {
                      const currentDataPoint = filteredData[currentMonthIndex];

                      return (
                        <ReferenceLine
                          x={currentDataPoint.month}
                          stroke="#2196f3"
                          strokeWidth={40}
                          strokeOpacity={0.15}
                        />
                      );
                    }
                    return null;
                  })()}

                {allLineKeys.map((key) => {
                  const group = selectedProvider === "ALL" ? key.split(" - ")[0] : selectedProvider;
                  const keysInGroup = allLineKeys.filter((k) => k.startsWith(group));
                  const isSingleLine = keysInGroup.length === 1;
                  const colorBase = hashKeyToColor(group);
                  const indexInGroup = keysInGroup.indexOf(key);
                  const strokeColor = isSingleLine
                    ? colorBase
                    : getVariantColor(colorBase, indexInGroup);

                  // Base color: group color if single line, otherwise key-specific color
                  const baseColor = isSingleLine ? hashKeyToColor(group) : hashKeyToColor(key);

                  // Check if this line should have a projection (exact case-insensitive match only)
                  // Also check if we're viewing the projection provider and this is the 'value' key
                  const projectionKey = `${key}_projected`;
                  const hasProjection =
                    showProjection &&
                    projectionLineKey &&
                    (key.toLowerCase() === projectionLineKey.toLowerCase() ||
                      (key === "value" &&
                        selectedProvider.toLowerCase() === projectionLineKey.toLowerCase()));

                  return (
                    <React.Fragment key={key}>
                      <Line
                        type="monotone"
                        dataKey={key}
                        stroke={strokeColor}
                        strokeWidth={2}
                        dot={{
                          stroke: strokeColor,
                          strokeWidth: 2.5,
                          r: 4,
                        }}
                        activeDot={{
                          r: 8,
                          onClick: (event, payload) => handleDataPointClick(event, payload),
                        }}
                      />
                      {/* Add projection dot for this specific line */}
                      {hasProjection &&
                        selectedProvider.toLowerCase() === projectionLineKey.toLowerCase() &&
                        (() => {
                          const now = new Date();
                          const currentYear = now.getFullYear();
                          const currentMonthNum = now.getMonth();
                          const monthNames = [
                            "Jan",
                            "Feb",
                            "Mar",
                            "Apr",
                            "May",
                            "Jun",
                            "Jul",
                            "Aug",
                            "Sep",
                            "Oct",
                            "Nov",
                            "Dec",
                          ];

                          const novData = filteredData.find((d) => {
                            const month = d.month || "";
                            return (
                              month.includes(`${currentYear}`) &&
                              (month.includes(`-${String(currentMonthNum + 1).padStart(2, "0")}`) ||
                                month.includes(monthNames[currentMonthNum]))
                            );
                          });

                          if (novData && novData[projectionKey]) {
                            return (
                              <ReferenceDot
                                x={novData.month}
                                y={novData[projectionKey]}
                                r={8}
                                fill="#ff9800"
                                fillOpacity={0.6}
                                stroke="#ff9800"
                                strokeWidth={3}
                                label={{
                                  value: `${currency ? `$${(novData[projectionKey] / 1000).toFixed(0)}K` : novData[projectionKey]} (projected)`,
                                  position: "top",
                                  fill: "#ff9800",
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  offset: 10,
                                }}
                              />
                            );
                          }
                          return null;
                        })()}
                    </React.Fragment>
                  );
                })}

                {/* Mean/Run Rate Line - Only for Total */}
                {showMeanLine && isMeanLineEnabled && meanValue !== null && (
                  <ReferenceLine
                    y={meanValue}
                    stroke="#4caf50"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    label={{
                      value: `Mean: ${currency ? `$${(meanValue / 1000).toFixed(0)}K` : meanValue.toFixed(0)}`,
                      position: "insideTopRight",
                      fill: "#4caf50",
                      fontSize: 12,
                      fontWeight: "bold",
                      offset: 10,
                    }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          <Grid
            sx={{
              alignItems: "center",
              ml: 2,
              maxWidth: "70px",
              position: "absolute",
              right: 10,
              top: 50,
              height: "100%",
            }}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              {showAll && (
                <IconButton
                  key={"ALL"}
                  size="small"
                  onClick={() => setSelectedProvider("ALL")}
                  sx={{
                    borderRadius: 2,
                    fontSize: 9,
                    fontWeight: "bold",
                    width: "70px",
                    backgroundColor: selectedProvider === "ALL" ? "#ccc" : "transparent",
                    color: selectedProvider === "ALL" ? "#000" : theme.palette.text.invert,

                    transition: "background-color 0.3s ease, color 0.3s ease",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                    "&:hover": {
                      backgroundColor:
                        selectedProvider === "ALL" ? "#ccc" : "rgba(255, 255, 255, 0.2)",
                      boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                >
                  {"ALL"}
                </IconButton>
              )}
              {providers.map((provider) => {
                const buttonColor = provider === "all" ? "#ccc" : hashKeyToColor(provider);

                return (
                  <IconButton
                    key={provider}
                    size="small"
                    onClick={() => setSelectedProvider(provider)}
                    sx={{
                      borderRadius: 2,
                      fontSize: 9,
                      fontWeight: "bold",
                      width: "70px",
                      backgroundColor: selectedProvider === provider ? buttonColor : "transparent",
                      color:
                        selectedProvider === provider
                          ? provider === "all"
                            ? "#000"
                            : theme.palette.text.invert
                          : theme.palette.text.primary,
                      transition: "background-color 0.3s ease, color 0.3s ease",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                      "&:hover": {
                        backgroundColor:
                          selectedProvider === provider ? buttonColor : "rgba(255, 255, 255, 0.2)",
                        boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.3)",
                      },
                    }}
                  >
                    {provider.toUpperCase()}
                  </IconButton>
                );
              })}
            </Box>

            {/* Mean Line Toggle - Separate from categories */}
            {showMeanLine && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <IconButton
                  key={"MEAN"}
                  size="small"
                  onClick={() => setIsMeanLineEnabled(!isMeanLineEnabled)}
                  sx={{
                    borderRadius: 2,
                    fontSize: 8,
                    fontWeight: "bold",
                    width: "70px",
                    backgroundColor: isMeanLineEnabled ? "#4caf50" : "transparent",
                    color: isMeanLineEnabled ? "#fff" : theme.palette.text.primary,
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    border: `2px solid ${isMeanLineEnabled ? "#4caf50" : "rgba(255, 255, 255, 0.2)"}`,
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                    "&:hover": {
                      backgroundColor: isMeanLineEnabled ? "#45a049" : "rgba(255, 255, 255, 0.2)",
                      boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                >
                  {"MEAN LINE"}
                </IconButton>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {renderCustomLegend(filteredData, selectedProvider, theme, finalColors)}
    </WidgetContainer>
  );
};

export default React.memo(LineGraph);
